import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './PromotionalBanner.css';

const PromotionalBanner = ({ promotion, disableAnimations = false }) => {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (promotion.show_countdown && promotion.end_date) {
      const timer = setInterval(() => {
        const now = new Date().getTime();
        const endTime = new Date(promotion.end_date).getTime();
        const difference = endTime - now;

        if (difference > 0) {
          const days = Math.floor(difference / (1000 * 60 * 60 * 24));
          const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((difference % (1000 * 60)) / 1000);

          setTimeLeft({ days, hours, minutes, seconds });
        } else {
          setTimeLeft(null);
          clearInterval(timer);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [promotion.show_countdown, promotion.end_date]);

  const bannerImage = promotion.banner_image || promotion.image;

  const BannerWrapper = disableAnimations ? 'div' : motion.div;
  const bannerProps = disableAnimations
    ? { className: "promotional-banner" }
    : {
        className: "promotional-banner",
        initial: { opacity: 0, y: -20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
      };

  return (
    <BannerWrapper {...bannerProps}>
      <div className="promotional-banner__container">
        <div
          className="promotional-banner__background"
          style={{ backgroundImage: `url(${bannerImage})` }}
        >
          <div className="promotional-banner__overlay"></div>
        </div>

        <div className="promotional-banner__content">
          {promotion.badge_text && (
            <motion.div
              className="promotional-banner__badge"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              {promotion.badge_text}
            </motion.div>
          )}

          <motion.h2
            className="promotional-banner__title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            {promotion.title}
          </motion.h2>

          {promotion.subtitle && (
            <motion.p
              className="promotional-banner__subtitle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              {promotion.subtitle}
            </motion.p>
          )}

          {promotion.description && (
            <motion.div
              className="promotional-banner__description"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.5 }}
              dangerouslySetInnerHTML={{ __html: promotion.description }}
            />
          )}

          <motion.div
            className="promotional-banner__pricing"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            {promotion.original_price && (
              <span className="promotional-banner__original-price">
                ${parseFloat(promotion.original_price).toFixed(2)}
              </span>
            )}
            <span className="promotional-banner__promotional-price">
              ${parseFloat(promotion.promotional_price).toFixed(2)}
            </span>
            {promotion.savings_percentage > 0 && (
              <span className="promotional-banner__savings">
                Save {promotion.savings_percentage}%
              </span>
            )}
          </motion.div>

          {timeLeft && (
            <motion.div
              className="promotional-banner__countdown"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.5 }}
            >
              <div className="countdown__label">Offer Ends In:</div>
              <div className="countdown__timer">
                {timeLeft.days > 0 && (
                  <div className="countdown__unit">
                    <span className="countdown__value">{timeLeft.days}</span>
                    <span className="countdown__text">Days</span>
                  </div>
                )}
                <div className="countdown__unit">
                  <span className="countdown__value">{String(timeLeft.hours).padStart(2, '0')}</span>
                  <span className="countdown__text">Hours</span>
                </div>
                <div className="countdown__unit">
                  <span className="countdown__value">{String(timeLeft.minutes).padStart(2, '0')}</span>
                  <span className="countdown__text">Minutes</span>
                </div>
                <div className="countdown__unit">
                  <span className="countdown__value">{String(timeLeft.seconds).padStart(2, '0')}</span>
                  <span className="countdown__text">Seconds</span>
                </div>
              </div>
            </motion.div>
          )}

          {promotion.cta_text && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              <Link
                to={promotion.cta_link || '/booking'}
                className="promotional-banner__cta"
              >
                {promotion.cta_text}
                <i className="fas fa-arrow-right"></i>
              </Link>
            </motion.div>
          )}
        </div>
      </div>
    </BannerWrapper>
  );
};

export default PromotionalBanner;
